import type { Resultado } from './acesso';
import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import { escreverNaApi as escrever, lerDaApi as ler } from './giro';
import type { Escrita } from './giro';

/**
 * QUADRO DE HORÁRIO (dono, 24/10/2026) — o front de /api/escala. Os tipos espelham
 * @inovapay/database (escala) e @inovapay/domain (escala).
 */

export type CategoriaDoTurno =
  'TRABALHO' | 'FOLGA' | 'COMPENSADA' | 'FERIADO' | 'FERIAS' | 'FALTA' | 'ATESTADO';

export interface TurnoDaEscala {
  readonly codigo: string;
  readonly rotulo: string;
  readonly categoria: CategoriaDoTurno;
  readonly marca: string;
  readonly corTexto: string;
  readonly corFundo: string | null;
  readonly entrada: string | null;
  readonly almocoInicio: string | null;
  readonly almocoFim: string | null;
  readonly saida: string | null;
  readonly ordem: number;
  readonly ativo: boolean;
}

export type ModoDeFolgaFixa = 'FIXA' | 'FIXA_DOMINGO' | 'DIAS';
export interface FolgaFixa {
  readonly modo: ModoDeFolgaFixa;
  /** FIXA/FIXA_DOMINGO: 1 = segunda … 6 = sábado. */
  readonly dia: number;
  /** DIAS: folga nesses dias toda semana (0 = domingo). */
  readonly diasFixos: readonly number[];
  readonly domingoBase: string | null;
  readonly cicloDomingo: number;
  readonly turnoPadrao: string | null;
  /** false = nos feriados fica FR (não trabalha). */
  readonly trabalhaFeriado: boolean;
  /** FIXA_DOMINGO: na semana do domingo de folga, folga também no dia fixo. */
  readonly folgaDupla: boolean;
}

export interface PessoaDaEscala {
  readonly id: string;
  readonly accountId: string | null;
  readonly nome: string;
  readonly ordem: number;
  readonly ativo: boolean;
  readonly folgaFixa: FolgaFixa | null;
  readonly admissao: string | null;
  readonly funcao: string | null;
  readonly aniversario: { readonly dia: number; readonly mes: number } | null;
  /** O horário da pessoa (vale mais que o do turno) e quem abre/fecha a loja. */
  readonly horario: HorarioDaPessoa;
  /** O turno de sempre (o mesmo da folga fixa). */
  readonly turnoPadrao: string | null;
}

export interface HorarioDaPessoa {
  readonly entrada: string | null;
  readonly almocoInicio: string | null;
  readonly almocoFim: string | null;
  readonly saida: string | null;
  readonly abreLoja: boolean;
  readonly fechaLoja: boolean;
}

/** As funções da loja (dono 24/10); a ficha também aceita outra escrita à mão. */
export const FUNCOES: readonly string[] = [
  'Balconista',
  'Caixa',
  'Gerente',
  'Estoquista',
  'Escritório',
  'Faxineira',
  'Farmacêutico(a)',
  'Entregador',
];

/* ---------- férias (dono 24/10) ---------- */
export interface BlocoDeFerias {
  readonly de: string;
  readonly ate: string;
  readonly dias: number;
}
export interface AjusteDeFerias {
  readonly id: string;
  readonly periodo: string;
  readonly dias: number;
  readonly motivo: string;
}
export type SituacaoDoPeriodo = 'EM_AQUISICAO' | 'ABERTO' | 'VENCENDO' | 'VENCIDO' | 'QUITADO';
export interface PeriodoDeFerias {
  readonly inicio: string;
  readonly fim: string;
  readonly limite: string;
  readonly faltas: number;
  readonly direito: number;
  readonly blocos: readonly BlocoDeFerias[];
  readonly ajustes: readonly AjusteDeFerias[];
  readonly usados: number;
  readonly saldo: number;
  readonly situacao: SituacaoDoPeriodo;
  readonly avisoFracionamento: string | null;
}
export interface FeriasDaPessoaNaEscala {
  readonly pessoaId: string;
  readonly nome: string;
  readonly admissao: string | null;
  readonly ferias: {
    readonly admissao: string;
    readonly periodos: readonly PeriodoDeFerias[];
    readonly saldo: number;
    readonly marcadas: readonly BlocoDeFerias[];
    readonly proximoVencimento: { readonly limite: string; readonly saldo: number } | null;
  } | null;
  readonly tiradas: readonly BlocoDeFerias[];
}
export const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDoPeriodo, string>> = {
  EM_AQUISICAO: 'Em aquisição',
  ABERTO: 'Pode tirar',
  VENCENDO: 'Vence logo',
  VENCIDO: 'Vencido',
  QUITADO: 'Tirou tudo',
};

export const DIAS_DA_FOLGA = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

export interface CelulaDaEscala {
  readonly pessoaId: string;
  readonly dia: string;
  readonly turno: string;
  readonly setor: string | null;
  readonly compensa: string | null;
  readonly observacao: string | null;
  /** O almoço DO DIA (revezamento dos caixas); null = vale o da ficha / do turno. */
  readonly almocoInicio?: string | null;
  readonly almocoFim?: string | null;
}

export interface MudancaDeCelula {
  readonly pessoaId: string;
  readonly dia: string;
  readonly turno: string | null;
  readonly setor?: string | null;
  readonly compensa?: string | null;
  readonly observacao?: string | null;
  readonly almocoInicio?: string | null;
  readonly almocoFim?: string | null;
}

export interface FeriadoDaEscala {
  readonly dia: string;
  readonly nome: string;
}

export interface QuadroDeHorario {
  readonly de: string;
  readonly ate: string;
  readonly hoje: string;
  readonly turnos: readonly TurnoDaEscala[];
  readonly pessoas: readonly PessoaDaEscala[];
  readonly celulas: readonly CelulaDaEscala[];
  readonly feriados: readonly FeriadoDaEscala[];
  /** Feriados de até 180 dias antes do período (para a FC apontar). */
  readonly feriadosParaCompensar: readonly FeriadoDaEscala[];
  readonly aniversariantes: readonly { nome: string; dia: number; mes: number }[];
  readonly atualizadoEm: string | null;
  readonly euPessoaId: string | null;
  readonly podeEditar: boolean;
}

export interface ControleDaPessoa {
  readonly pessoaId: string;
  readonly nome: string;
  readonly trabalhados: number;
  readonly folgas: number;
  readonly domingosTrabalhados: number;
  readonly faltas: number;
  readonly atestados: number;
  readonly ferias: number;
  readonly feriadosTrabalhados: readonly string[];
  readonly compensadas: readonly { readonly dia: string; readonly feriado: string | null }[];
  readonly feriadosAPagar: readonly string[];
  readonly proximasFerias: { readonly de: string; readonly ate: string } | null;
}

export type TipoDePedido = 'FOLGA' | 'TROCA' | 'FERIAS' | 'ATESTADO';
export type EstadoDoPedido = 'PENDENTE' | 'APROVADO' | 'RECUSADO' | 'CANCELADO';

export interface PedidoDaEscala {
  readonly id: string;
  readonly tipo: TipoDePedido;
  readonly pessoaId: string;
  readonly pessoa: string;
  readonly pessoaAccountId: string | null;
  readonly pedidoPor: string;
  readonly de: string;
  readonly ate: string;
  readonly trocaDia: string | null;
  readonly trocaPessoaId: string | null;
  readonly trocaPessoa: string | null;
  readonly compensa: string | null;
  readonly motivo: string | null;
  readonly estado: EstadoDoPedido;
  readonly resposta: string | null;
  readonly decididoPor: string | null;
  readonly decididoEm: string | null;
  readonly criadoEm: string;
  readonly arquivos: readonly {
    readonly id: string;
    readonly mime: string;
    readonly tamanho: number;
  }[];
}

export interface AlteracaoDaEscala {
  readonly id: number;
  readonly pessoaId: string;
  readonly pessoa: string;
  readonly dia: string;
  readonly antes: { readonly turno: string; readonly setor: string | null } | null;
  readonly depois: { readonly turno: string; readonly setor: string | null } | null;
  readonly por: string;
  readonly em: string;
  readonly pedidoId: string | null;
}

export const ROTULO_DO_PEDIDO: Readonly<Record<TipoDePedido, string>> = {
  FOLGA: 'Folga',
  TROCA: 'Troca de folga',
  FERIAS: 'Férias',
  ATESTADO: 'Atestado',
};
export const ROTULO_DO_ESTADO: Readonly<Record<EstadoDoPedido, string>> = {
  PENDENTE: 'Aguardando',
  APROVADO: 'Aprovado',
  RECUSADO: 'Recusado',
  CANCELADO: 'Cancelado',
};

/* ------------------------------------------------------------------ *
 * Datas (semana do quadro: segunda a domingo — dono 26/10)
 * ------------------------------------------------------------------ */

const MS_DIA = 86_400_000;
export const somarDias = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * MS_DIA).toISOString().slice(0, 10);
export const diaDaSemana = (dia: string) => new Date(`${dia}T12:00:00Z`).getUTCDay();
export const inicioDaSemana = (dia: string) => somarDias(dia, -((diaDaSemana(dia) + 6) % 7));
export const hojeBrasilia = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
export function diasDoPeriodo(de: string, ate: string): string[] {
  const n = Math.round((Date.parse(`${ate}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / MS_DIA);
  return Array.from({ length: Math.max(0, n + 1) }, (_, i) => somarDias(de, i));
}
const SEMANA = ['DOM', '2a', '3a', '4a', '5a', '6a', 'SAB'];
const SEMANA_LONGA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
export const siglaDoDia = (dia: string) => SEMANA[diaDaSemana(dia)] ?? '';
export const nomeDoDia = (dia: string) => SEMANA_LONGA[diaDaSemana(dia)] ?? '';
export const mesCurto = (dia: string) => MESES[Number(dia.slice(5, 7)) - 1] ?? '';
export const diaMes = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
export const diaLongo = (dia: string) => `${nomeDoDia(dia)}, ${diaMes(dia)}`;

/* ------------------------------------------------------------------ *
 * Chamadas
 * ------------------------------------------------------------------ */

const qs = (p: Record<string, string | null | undefined>) => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : '';
};

export const lerQuadro = (de?: string, ate?: string) =>
  ler<QuadroDeHorario>(`/api/escala${qs({ de, ate })}`);

export const lerEu = () =>
  ler<{
    pessoa: PessoaDaEscala | null;
    pedidos: PedidoDaEscala[];
    controle: ControleDaPessoa | null;
    hoje: string;
    podeEditar: boolean;
    ferias?: FeriasDaPessoaNaEscala | null;
  }>('/api/escala/eu');

export const gravarCelulas = (s: Sessao, mudancas: readonly MudancaDeCelula[]) =>
  escrever<{ alteradas: number }>(s, 'PUT', '/api/escala/celulas', { mudancas });

export const lerHistorico = (pessoa?: string) =>
  ler<{ alteracoes: AlteracaoDaEscala[] }>(`/api/escala/historico${qs({ pessoa })}`);

export const lerControle = (de: string, ate: string) =>
  ler<{ de: string; ate: string; pessoas: ControleDaPessoa[] }>(
    `/api/escala/controle${qs({ de, ate })}`,
  );

export const alterarTurno = (s: Sessao, codigo: string, m: Partial<TurnoDaEscala>) =>
  escrever<TurnoDaEscala>(s, 'PUT', `/api/escala/turnos/${codigo}`, m);
export const criarTurno = (
  s: Sessao,
  n: { codigo: string; rotulo: string; corTexto: string; corFundo: string | null },
) => escrever<TurnoDaEscala>(s, 'POST', '/api/escala/turnos', n);

export const lerPessoas = () =>
  ler<{
    pessoas: PessoaDaEscala[];
    contasFora: { accountId: string; nome: string; login: string }[];
  }>('/api/escala/pessoas');
export const incluirPessoa = (s: Sessao, p: { accountId?: string; nome?: string }) =>
  escrever<PessoaDaEscala>(s, 'POST', '/api/escala/pessoas', p);
/** Trocou o turno de sempre: quantos X de hoje em diante passaram para o turno novo. */
export type PessoaComDiasTrocados = PessoaDaEscala & { readonly diasTrocados?: number };
export const alterarPessoa = (
  s: Sessao,
  id: string,
  m: {
    nome?: string;
    ativo?: boolean;
    accountId?: string | null;
    funcao?: string | null;
    aniversario?: { dia: number; mes: number } | null;
    horario?: HorarioDaPessoa;
    turnoPadrao?: string | null;
  },
) => escrever<PessoaComDiasTrocados>(s, 'PUT', `/api/escala/pessoas/${id}`, m);
export const reordenarPessoas = (s: Sessao, ids: readonly string[]) =>
  escrever<{ pessoas: PessoaDaEscala[] }>(s, 'PUT', '/api/escala/pessoas-ordem', { ids });

export const definirFolgaFixa = (s: Sessao, id: string, folgaFixa: FolgaFixa | null) =>
  escrever<PessoaComDiasTrocados>(s, 'PUT', `/api/escala/pessoas/${id}/folga-fixa`, {
    folgaFixa,
  });
export const aplicarFolgas = (
  s: Sessao,
  o: { de: string; ate: string; substituir: boolean; pessoaIds?: readonly string[] | null },
) => escrever<{ alteradas: number; pessoas: number }>(s, 'POST', '/api/escala/aplicar-folgas', o);

export const lerFerias = () =>
  ler<{ hoje: string; pessoas: FeriasDaPessoaNaEscala[] }>('/api/escala/ferias');
export const lerCalendarioDeFerias = (de?: string, ate?: string) =>
  ler<{
    de: string;
    ate: string;
    hoje: string;
    ferias: { pessoaId: string; dia: string }[];
    outras: { pessoaId: string; dia: string; turno: string }[];
    feriados: FeriadoDaEscala[];
  }>(`/api/escala/ferias/calendario${qs({ de, ate })}`);
export const definirAdmissao = (s: Sessao, pessoaId: string, admissao: string | null) =>
  escrever<{ pessoa: FeriasDaPessoaNaEscala | null }>(
    s,
    'PUT',
    `/api/escala/pessoas/${pessoaId}/admissao`,
    { admissao },
  );
export const incluirAjusteDeFerias = (
  s: Sessao,
  a: { pessoaId: string; periodo: string; dias: number; motivo: string },
) =>
  escrever<{ pessoa: FeriasDaPessoaNaEscala | null }>(s, 'POST', '/api/escala/ferias/ajustes', a);
export const removerAjusteDeFerias = (s: Sessao, id: string) =>
  escrever<{ removido: string }>(s, 'POST', `/api/escala/ferias/ajustes/${id}/remover`);

/** `ajustadas` = quantos dias viraram FR (ou deixaram de ser) para quem não trabalha feriado. */
export const incluirFeriado = (s: Sessao, f: FeriadoDaEscala) =>
  escrever<FeriadoDaEscala & { ajustadas?: number }>(s, 'POST', '/api/escala/feriados', f);
export const removerFeriado = (s: Sessao, dia: string) =>
  escrever<{ removido: string; ajustadas?: number }>(s, 'POST', '/api/escala/feriados/remover', {
    dia,
  });

export const lerPedidos = (estado?: EstadoDoPedido) =>
  ler<{ pedidos: PedidoDaEscala[] }>(`/api/escala/pedidos${qs({ estado })}`);
export const criarPedido = (
  s: Sessao,
  p: {
    chave: string;
    tipo: TipoDePedido;
    pessoaId?: string | null;
    de: string;
    ate: string;
    trocaDia?: string | null;
    trocaPessoaId?: string | null;
    compensa?: string | null;
    motivo?: string | null;
  },
) => escrever<PedidoDaEscala>(s, 'POST', '/api/escala/pedidos', p);
export const cancelarPedido = (s: Sessao, id: string) =>
  escrever<PedidoDaEscala>(s, 'POST', `/api/escala/pedidos/${id}/cancelar`);
export const decidirPedido = (s: Sessao, id: string, aprovar: boolean, resposta: string | null) =>
  escrever<PedidoDaEscala>(s, 'POST', `/api/escala/pedidos/${id}/decidir`, { aprovar, resposta });

export const urlDoArquivo = (id: string) => `/api/escala/arquivos/${id}`;

/** O atestado vai como o próprio arquivo no corpo (sem multipart). */
export async function anexarArquivo(
  s: Sessao,
  pedidoId: string,
  arquivo: File,
): Promise<Escrita<PedidoDaEscala>> {
  let r: Response;
  try {
    r = await fetch(`/api/escala/pedidos/${pedidoId}/arquivo`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'content-type': arquivo.type || 'application/octet-stream',
        [CABECALHO_CSRF]: s.csrfToken,
      },
      body: arquivo,
    });
  } catch {
    return { tipo: 'incerta' };
  }
  if (r.ok) return { tipo: 'confirmada', dados: (await r.json()) as PedidoDaEscala };
  if (r.status >= 500) return { tipo: 'incerta' };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 413) mensagem = 'Arquivo grande demais (até 8 MB).';
  if (r.status === 415) mensagem = 'Mande foto (JPG, PNG, WEBP) ou PDF.';
  return { tipo: 'recusada', mensagem: mensagem || 'Não foi aceito.', status: r.status };
}

export type { Escrita, Resultado };

/* ------------------------------------------------------------------ *
 * Metas da equipe (aba Metas) — só leitura, só quem edita o quadro
 * ------------------------------------------------------------------ */
export type PorDoGrafico = 'dia' | 'semana' | 'mes';
export interface IndicadorDaEquipe {
  codigo: string;
  rotulo: string;
  unidade: 'RS' | 'PCT' | 'UN' | 'RAZAO';
  casas: number;
  natureza: 'RAZAO' | 'SOMA';
  direcao: 'MAIOR_MELHOR' | 'MENOR_MELHOR';
}
export interface MetaDoIndicador {
  alvo: number;
  alvoFinal: number;
  nivel: number;
  nivelMaximo: number;
  nome: string;
}
export interface NumerosPorIndicador {
  valores: Record<string, number | null>;
  anteriores: Record<string, number | null>;
  serie: Record<string, (number | null)[]>;
}
export interface PessoaNasMetas extends NumerosPorIndicador {
  pessoaId: string;
  nome: string;
  funcao: string | null;
  accountId: string | null;
  semConta: boolean;
  metas: Record<string, MetaDoIndicador>;
}
export interface MetasDaEquipe {
  de: string;
  ate: string;
  anterior: { de: string; ate: string };
  por: PorDoGrafico;
  indicadores: IndicadorDaEquipe[];
  blocos: { chave: string; rotulo: string; de: string; ate: string }[];
  periodoEhMes: boolean;
  pessoas: PessoaNasMetas[];
  equipe: NumerosPorIndicador;
  agregadoAte: string | null;
}
export const lerMetasDaEquipe = (de: string, ate: string, por: PorDoGrafico) =>
  ler<MetasDaEquipe>(`/api/escala/metas${qs({ de, ate, por })}`);
